import { Chip, Stack } from '@mui/material';

import { CaptionText } from '../../shared/typography/PrimaryTypography';
import { EsgRecordStatus } from '../../../types/syntiro_honey';

/**
 * Status colours are deliberately restricted to the two TERMINAL states.
 *
 * Amber previously carried eight unrelated meanings in one row (suggested mapping,
 * medium confidence, flagged, disambiguate, unmapped, untrusted value, follow-up
 * marker, skipped sheets), so a row of three amber chips read as uniformly alarming
 * and none of it read as actionable. Confidence keeps the traffic-light scale — it
 * is a real ordinal band and earns it — and every in-progress status goes neutral.
 * What still needs doing is signalled by the confidence band and by the presence of
 * an action, not by a third colour competing with both.
 */
const STATUS_DISPLAY: Record<
  EsgRecordStatus,
  { label: string; color: 'default' | 'success' | 'error' }
> = {
  PENDING_REVIEW: { label: 'Pending review', color: 'default' },
  // Short enough to never truncate in the grid's status column.
  NEEDS_DISAMBIGUATION: { label: 'Disambiguate', color: 'default' },
  FLAGGED: { label: 'Flagged', color: 'default' },
  APPROVED: { label: 'Approved', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'error' }
};

/**
 * Builds the "approved by X on <date>" attribution line for an approved record,
 * or null when the record is not approved / has no recorded approver.
 *
 * Prefers the approver's display name over their uid: a raw SSO uid read as
 * gibberish to reviewers (Maxine 2026-08-03 ⑩). The uid remains the fallback, so
 * an unresolvable user still yields an attribution rather than "a reviewer".
 * @param {string | null} approvedById The approver's SSO uid.
 * @param {string | null} approvedAt The approval timestamp (ISO 8601).
 * @param {string | null} [approvedByName] The approver's resolved display name.
 * @returns {string | null} The attribution line, or null.
 */
export function approvalAttribution(
  approvedById: string | null,
  approvedAt: string | null,
  approvedByName?: string | null
): string | null {
  if (approvedById === null && approvedAt === null) {
    return null;
  }
  const who = approvedByName ?? approvedById ?? 'a reviewer';
  const when = approvedAt === null ? '' : ` on ${new Date(approvedAt).toLocaleDateString()}`;
  return `Approved by ${who}${when}`;
}

interface RecordStatusChipProps {
  /** The review status to render. */
  status: EsgRecordStatus;
  /** Approver SSO uid (the attribution fallback when no name resolved), or null. */
  approvedById?: string | null;
  /** Approval timestamp (ISO 8601), or null. */
  approvedAt?: string | null;
  /** Approver's resolved display name, preferred over the uid in the attribution. */
  approvedByName?: string | null;
}

/**
 * Outlined chip showing an ESG record's review status with a status-appropriate
 * colour. When the record is approved and carries approver attribution, a caption
 * under the chip names who approved it and when — so a locked row says not just
 * "done" but "done by whom". Visible text, not a tooltip: the attribution matters
 * to keyboard and screen-reader users too, and a Chip cannot take focus.
 * @param {RecordStatusChipProps} props The status and optional approver attribution.
 * @returns {JSX.Element}
 */
export default function RecordStatusChip({
  status,
  approvedById = null,
  approvedAt = null,
  approvedByName = null
}: RecordStatusChipProps) {
  const display = STATUS_DISPLAY[status];
  const chip = <Chip label={display.label} size="small" variant="outlined" color={display.color} />;
  const attribution =
    status === 'APPROVED' ? approvalAttribution(approvedById, approvedAt, approvedByName) : null;
  if (attribution === null) {
    return chip;
  }
  return (
    <Stack spacing={0.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
      {chip}
      <CaptionText noWrap sx={{ maxWidth: '100%' }}>
        {attribution}
      </CaptionText>
    </Stack>
  );
}
